import React from "react";
import type { ThoughtDTO } from "@/generated-client";
import { MetaHandler } from "@/main/common/MetaHandler";
import {
    getViewerActionRecord,
    rememberViewerActionRecord,
    type ShortPostInlineReplyRecord,
} from "@/main/common/ViewerActionRecords";
import type { Localizer } from "@/main/react/Types";
import ThoughtTextEditor from "@/main/react/editor/thought/ThoughtTextEditorLazy";

const BLOCK_SELECTOR = "p,div,li,h1,h2,h3,h4,h5,h6,blockquote,pre,figcaption";

/**
 * A published thought's HTML as plain text, for the inline render under the card: decoded through an
 * inert parsed document (never attached, so nothing in it loads or runs), one line per block. Media and
 * embeds are not drawn inline; the "View" link opens the thought on its post's page.
 */
export function thoughtHtmlToPlainText(html: string): string {
    if (!html || typeof DOMParser === "undefined") {
        return "";
    }
    const body = new DOMParser().parseFromString(html, "text/html").body;
    body.querySelectorAll("br").forEach((element) => element.replaceWith("\n"));
    body.querySelectorAll(BLOCK_SELECTOR).forEach((element) => element.after("\n"));
    return (body.textContent ?? "")
        .split("\n")
        .map((line) => line.replace(/[ \t ]+/g, " ").trim())
        .filter(Boolean)
        .join("\n");
}

/**
 * The reply bar under a short post's feed card (round 31 D10; SV-147 A4). Since SV-147 a reply to a short
 * post is a THOUGHT, so the bar is the compact Thought composer (`ThoughtTextEditor`), publishing a
 * TOP-LEVEL thought on this post through the composer's own `ClientFactory.publishThought`. It rests as one
 * line with no toolbar (the composer's progressive disclosure, SV-145): no `autoFocus`, no `roomy`, no
 * stance chips. The placeholder reuses `thought.editor.placeholder.reply` so the bar never reads as
 * "publish a new post"; the composer's own Reply button submits. D3: short cards only (the card decides).
 *
 * <p><b>A reply stays in the feed (SV-141 D3).</b> No navigation: the composer clears and the thought
 * renders directly under it, as text, with a "View" link (`literal.view`) to the thought on its post's
 * page (`/discussion/{postPublicId}#argument-{thoughtPublicId}`). It is banked in `ViewerActionRecords`
 * (`short-post-inline-replies`, keyed by THIS post's publicId, retyped to thoughts by SV-147 A5) because
 * the card is drawn from a list DTO that a Back-restored feed serves stale (rule 33); the bar seeds from
 * the record on mount. One entry per thought `publicId`, so a repeated answer renders once.
 */
export function ShortPostReplyBox(props: {
    discussionPublicId: string;
    localizer: Localizer;
}) {
    const { discussionPublicId, localizer } = props;
    // The card mounts this bar only for a signed-in viewer; the name is the one signal both read.
    const loggedInUsername = MetaHandler.getLoggedInUsername() ?? undefined;

    const [replies, setReplies] = React.useState<readonly ShortPostInlineReplyRecord[]>(
        () => getViewerActionRecord("short-post-inline-replies", discussionPublicId) ?? [],
    );
    // A card re-used for another post re-seeds from THAT post's record.
    const [seededFor, setSeededFor] = React.useState(discussionPublicId);
    if (seededFor !== discussionPublicId) {
        setSeededFor(discussionPublicId);
        setReplies(getViewerActionRecord("short-post-inline-replies", discussionPublicId) ?? []);
    }

    const repliesRef = React.useRef(replies);
    repliesRef.current = replies;

    const handlePublished = React.useCallback(
        (published: ThoughtDTO) => {
            const thoughtPublicId = published.publicId;
            // A thought already drawn adds nothing and changes no identity.
            if (!thoughtPublicId || repliesRef.current.some((reply) => reply.thoughtPublicId === thoughtPublicId)) {
                return;
            }
            const next: readonly ShortPostInlineReplyRecord[] = [
                ...repliesRef.current,
                {
                    thoughtPublicId,
                    permalink: `/discussion/${discussionPublicId}#argument-${thoughtPublicId}`,
                    text: thoughtHtmlToPlainText(published.htmlContent ?? ""),
                    authorUsername: published.username ?? loggedInUsername ?? "",
                },
            ];
            rememberViewerActionRecord("short-post-inline-replies", discussionPublicId, next);
            repliesRef.current = next;
            setReplies(next);
        },
        [discussionPublicId, loggedInUsername],
    );

    return (
        <div className="mt-2" data-playwright-id="short-post-reply-box">
            <ThoughtTextEditor
                localizer={localizer}
                isUserLoggedIn={Boolean(loggedInUsername)}
                loggedInUsername={loggedInUsername}
                discussionPublicId={discussionPublicId}
                // Stance titles feed only the stance chips, which this bar never shows.
                agreementAgreementTitle=""
                disagreementAgreementTitle=""
                paragraphPlaceholderKey="thought.editor.placeholder.reply"
                onThoughtPublished={handlePublished}
                playwrightIdPrefix="short-post-reply-box"
                hideAvatar={true}
            />
            {replies.length > 0 ? (
                <ul className="mt-2 flex flex-col gap-2" data-playwright-id="short-post-inline-replies">
                    {replies.map((reply) => (
                        <li
                            key={reply.thoughtPublicId}
                            className="rounded-md border border-border px-3 py-2 text-sm"
                            data-playwright-id="short-post-inline-reply"
                        >
                            <div className="flex items-center justify-between gap-2">
                                {reply.authorUsername ? (
                                    <span className="font-medium text-foreground" translate="no">
                                        {reply.authorUsername}
                                    </span>
                                ) : <span />}
                                <a
                                    href={reply.permalink}
                                    className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                                    data-playwright-id="short-post-inline-reply-view"
                                >
                                    {localizer("literal.view")}
                                </a>
                            </div>
                            <p className="mt-1 whitespace-pre-line break-words text-foreground">{reply.text}</p>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
